import { Button, FormControl, FormControlLabel, Radio, RadioGroup, Stack, type Theme, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import Alert from '/@/components/bayer/alert/index.tsx';
import ModalV2 from '/@/components/bayer/modal-v2';
import Tabs from '/@/components/bayer/tabs';
import {
    type RVAdvisorInvitationApi,
    useAcceptAdvisorRvInvitation,
    useAcceptAllAdvisorRvInvitations,
    useAdvisorRvInvitations,
    useDeclineAdvisorRvInvitation,
    useDeclineAllAdvisorRvInvitations,
} from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { modalBodyPropsWide, modalProps } from '../index.tsx';
import ReceivedInvitations from './received-invitations.tsx';
import SendedInvitations from './sended-invitations';

const RVOnboardingModalInvites = ({
    open,
    onClose,
    onInviteMoreAdvisors,
}: {
    open: boolean;
    onClose: () => void;
    onInviteMoreAdvisors?: () => void;
}) => {
    const [tab, setTab] = useState<'sent' | 'received'>('received');
    const listInvitationsQuery = useAdvisorRvInvitations({
        direction: tab,
    });

    const pendingInvitations = useMemo(
        () => listInvitationsQuery.data?.results?.filter((invitation) => invitation.status === 'pending') || [],
        [listInvitationsQuery.data?.results]
    );

    // Show message only if no invites or all invites are from today
    const shouldShowFirstTimeMessage = useMemo(() => {
        const invites =
            listInvitationsQuery.data?.results?.filter((invitation) => invitation.status !== 'cancelled') || [];
        if (!invites || invites.length === 0) return false;

        const today = dayjs().startOf('day');
        return invites.every((invite) => {
            const createdDate = dayjs(invite.created);
            return createdDate.isAfter(today) || createdDate.isSame(today);
        });
    }, [listInvitationsQuery.data?.results]);

    const acceptAllAdvisorRvInvitations = useAcceptAllAdvisorRvInvitations();

    const tabs = {
        received: {
            label: _('Recibidas'),
            component: (
                <Stack spacing={6} sx={{ p: 5, width: '100%' }}>
                    {shouldShowFirstTimeMessage && (
                        <Typography variant='body1' color='primary'>
                            {_(
                                'Si aceptas una solicitud, vos y la persona que te la envió van a compartir la información de los productores Premium que tienen en común. Si la rechazas, no se va a compartir información.'
                            )}
                        </Typography>
                    )}
                    <ReceivedInvitations
                        invitations={listInvitationsQuery.data?.results || []}
                        loading={listInvitationsQuery.isLoading}
                    />

                    {(pendingInvitations?.length || 0) > 0 && (
                        <Stack alignItems='flex-end'>
                            <Button
                                variant='text'
                                color='secondary'
                                onClick={() => {
                                    acceptAllAdvisorRvInvitations.trigger();
                                }}
                                disabled={pendingInvitations?.length === 0}
                            >
                                {_('Aceptar todas las solicitudes')}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            ),
        },
        sent: {
            label: _('Enviadas'),
            component: (
                <Stack spacing={6} sx={{ p: 5, width: '100%' }}>
                    {shouldShowFirstTimeMessage && (
                        <Typography variant='body1' color='primary'>
                            {_(
                                'Una vez que los Asesores acepten tu solicitud, podrán trabajar en conjunto sobre la misma información de los productores Premium que comparten, crear y ver las mismas recomendaciones y acceder a los datos de los lotes y su rendimiento.'
                            )}
                        </Typography>
                    )}

                    <SendedInvitations
                        invitations={
                            listInvitationsQuery.data?.results?.filter(
                                (invitation) => invitation.status !== 'cancelled'
                            ) || []
                        }
                        loading={listInvitationsQuery.isLoading}
                    />
                </Stack>
            ),
        },
    };

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            actions={
                <Button variant='contained' color='secondary' onClick={onInviteMoreAdvisors}>
                    {_('Enviar solicitud de vinculación')}
                </Button>
            }
            {...modalProps}
            maxWidth='sm'
            bodyProps={{
                ...modalProps.bodyProps,
                ...modalBodyPropsWide,
                sx: {
                    p: 0,
                    width: '100%',
                },
                spacing: 0,
            }}
            title={_('Solicitudes de vinculación')}
        >
            <Tabs
                options={Object.entries(tabs).map(([value, tab]) => ({
                    label: tab.label,
                    value,
                }))}
                value={tab}
                setValue={(value) => setTab(value as 'sent' | 'received')}
            />
            {tabs[tab].component}
        </ModalV2>
    );
};

export default RVOnboardingModalInvites;

export const RVOnboardingModalRVPendingInvites = ({
    open,
    onClose,
    onInviteMoreRvs,
}: {
    open: boolean;
    onClose: () => void;
    onInviteMoreRvs: () => void;
}) => {
    const advisorsRvInvitationsQuery = useAdvisorRvInvitations({ status: 'pending', direction: 'received' });

    const [selectedInvitation, setSelectedInvitation] = useState<RVAdvisorInvitationApi | null>(null);

    const acceptRvInvitation = useAcceptAdvisorRvInvitation();
    const declineRvInvitation = useDeclineAdvisorRvInvitation({ showSnackbar: false });
    const declineAllMutation = useDeclineAllAdvisorRvInvitations();

    const handleAcceptInvitation = async () => {
        if (!selectedInvitation?.id) return;

        await acceptRvInvitation.trigger(selectedInvitation.id);

        const otherInvitations = advisorsRvInvitationsQuery.data?.results?.filter(
            (invitation) => invitation.id !== selectedInvitation.id
        );
        if (otherInvitations) {
            await Promise.all(otherInvitations.map((invitation) => declineRvInvitation.trigger(invitation.id)));
        }

        onClose();
    };

    const handleDeclineAllAndNavigate = async () => {
        await declineAllMutation.trigger();
        onInviteMoreRvs();
    };

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            actions={
                <Stack direction='row' spacing={2} justifyContent='space-between' sx={{ width: '100%' }}>
                    <Button
                        variant='text'
                        color='secondary'
                        onClick={handleDeclineAllAndNavigate}
                        disabled={declineAllMutation.isMutating}
                    >
                        {_('No trabajo con ninguno de estos RV')}
                    </Button>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={handleAcceptInvitation}
                        disabled={!selectedInvitation?.id}
                    >
                        {_('Aceptar solicitud')}
                    </Button>
                </Stack>
            }
            {...modalProps}
            maxWidth='sm'
            bodyProps={{
                ...modalProps.bodyProps,
                ...modalBodyPropsWide,
                sx: {
                    p: 0,
                    width: '100%',
                },
                spacing: 0,
            }}
            title={_('Solicitudes de vinculación')}
        >
            <Stack spacing={3} sx={{ p: 5, width: '100%' }}>
                <Alert severity='blue' sx={{ width: '100%', alignItems: 'center' }}>
                    <Stack direction='row' alignItems='center' flexWrap='nowrap' spacing={0.5}>
                        <Typography color='primary' variant='body1' fontWeight='bold'>
                            {_('Importante: ')}
                        </Typography>
                        <Typography color='primary' variant='body1'>
                            {_('Recordá que solo podes elegir un RV.')}
                        </Typography>
                    </Stack>
                </Alert>

                <Typography variant='body1' color='primary'>
                    {_(
                        'Seleccioná al RV con quien trabajás entre las siguientes opciones. Si aceptas la solicitud, ambos van a ver y trabajar con la información de los productores Premium que comparten.'
                    )}
                </Typography>
                <FormControl sx={{ maxHeight: '150px', overflow: 'auto', width: '100%' }}>
                    <RadioGroup sx={{ width: '100%' }}>
                        {advisorsRvInvitationsQuery.data?.results?.map((invitation, index) => (
                            <FormControlLabel
                                key={invitation.id}
                                value={invitation.id}
                                checked={selectedInvitation?.id === invitation.id}
                                control={<Radio color='secondary' sx={{ ml: 1 }} />}
                                sx={{
                                    width: '100%',
                                    m: 0,
                                    py: 1,
                                    px: 1,
                                    '& .MuiFormControlLabel-label': {
                                        flex: 1,
                                    },
                                    border: (t: Theme) => `1px solid ${t.palette.divider}`,
                                    mt: index === 0 ? 0 : '-1px',
                                    ...(selectedInvitation?.id === invitation.id && {
                                        backgroundColor: 'grey.100',
                                    }),
                                }}
                                label={
                                    <Typography className='mp-mask' variant='body1' color='primary'>
                                        {invitation.rv.name}
                                    </Typography>
                                }
                                onClick={() => {
                                    setSelectedInvitation(invitation);
                                }}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Stack>
        </ModalV2>
    );
};
